import { defineComponent as _defineComponent } from 'vue'
import '@/components/article-editor/ArticleEditor/ArticleEditor.scss';

import ArticleTagsDialog from '@/components/layout/dialogs/ArticleTagsDialog.vue';
import ArticleTextEditor from '@/components/article-editor/ArticleTextEditor.vue';
import UploadButton from '@/components/screen-recorder/UploadButton.vue';
import EmptyStateCard from '@/components/layout/cards/EmptyStateCard.vue';
import { ArticleVideoEditorState } from '@/components/article-editor/types/ArticleVideoEditorState';
import ArticlePlayer from '@/components/article-player/ArticlePlayer.vue';
import EditorNavbar from '@/components/article-editor/EditorNavbar/EditorNavbar.vue';

import EventBus from '@/EventBus';
import { Logger } from '@/other/Logger';
import store from '@/store';
import router from '@/router';
import { DRAFTS, RETENTION } from '@/routerNames';
import navigationService from '@/services/NavigationService';
import { commitSetArticleEditorIsPublishingDisabled } from '@/store/commits/articleEditorCommits';

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { dispatchDeleteArticle } from '@/store/dispatchers/articleDispatchers';
import { ArticleEditorState } from '@/store/state/ArticleEditorState';
import articleAddRecordingService from '@/services/article/ArticleAddRecordingService';
import notificationService from '@/services/NotificationService';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import articleEditService from '@/services/article/ArticleEditService';
import draftService from '@/services/draft/DraftService';
import apiService from '@/services/ApiService';
import { ExtensionMessageType } from '@/@types/enums/ExtensionMessageType';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { Article } from '@/models/article/Article';
import { Range } from '@/models/article/Range';
import { Workspace } from '@/models/Workspace';
import { Paragraph } from '@/models/article/Paragraph';
import extensionService from '@/services/ExtensionService';
import { PostMessage } from '@/other/PostMessage';
import { SearchByTextProperty } from '@/models/SearchByText';


export default /*#__PURE__*/_defineComponent({
  __name: 'ArticleEditor',
  setup(__props) {

const log = new Logger('ArticleEditor');

const articleUndoStack = ref<Article[]>([]);
const isDeleting = ref(false);
const timeout = ref<ReturnType<typeof setTimeout> | null>(null);
const dynamicTextEditorKey = ref('');

const uploadButton = ref<UploadButton>();
const videoPlayer = ref<ArticlePlayer>();

const getArticleEditor = computed((): ArticleEditorState => articleEditService.articleEditor);
const getV = computed((): SearchByTextProperty[] => store.state.articleTextPropsToFind);
const getArticleMode = computed((): ArticleEditMode => store.state.articleMode);
const getIsOcrMode = computed((): boolean => store.state.ui.isOcrModeEnabled);
const getIsMobile = computed((): boolean => mobileResponsiveService.isMobileView);

const editorKey = computed((): string => dynamicTextEditorKey.value);

const getIsDefaultVideoEditorState = computed((): boolean =>
  [ArticleVideoEditorState.recording, ArticleVideoEditorState.inserting].includes(getArticleEditor.value.state)
);

const setReferences = () => articleEditService.setState('videoPlayer', videoPlayer.value);

const chooseFile = () => uploadButton.value.chooseFile();

const videoTimeUpdate = (ms: number) => articleEditService.handleVideoTimeUpdate(ms);

const trim = (range: Range) => articleEditService.handleTrim(range);

const moveParagraph = (data: any) => articleEditService.handleMoveParagraph(data);

const paragraphPlaying = (data: { paragraph: Paragraph; progress: number }) =>
  articleEditService.handleParagraphPlaying(data);

const playerState = (state: any) => articleEditService.handlePlayerState(state);

const playedRange = (range: Range) => articleEditService.handlePlayedRange(range);

const videoDurationDetected = (data: any) => articleEditService.handleVideoDurationDetected(data);

const insertParagraph = () => articleEditService.handleInsertParagraph();

const onClickAddRecording = async () => await articleAddRecordingService.handleRecordVideoForArticle();

const onMessageFromExtension = (postMessage: PostMessage) => {
  const isVideoProcessing = store.state.articleEditor.isVideoProcessing;
  const editorState = getArticleEditor.value.state;

  if (editorState === ArticleVideoEditorState.inserting) {
    switch (postMessage.message) {
      case ExtensionMessageType.RECORDING_UPLOAD_PROGRESS:
        if (!isVideoProcessing) {
          store.commit('setArticleEditorIsVideoProcessing', true);
        }
        break;
      case ExtensionMessageType.RECORDING_CANCELED: {
        store.commit('setArticleEditorIsVideoProcessing', false);
        store.commit('setArticleEditorState', ArticleVideoEditorState.default);
        window.location.reload();
      }
    }
  }
};

const deleteArticle = () => {
  const article = getArticleEditor.value.article;
  if (!article.id) return;

  if (!article.id && !article.draft) {
    isDeleting.value = true;
    draftService.deleteDraft(article, { cb: redirectAfterDelete, handleCancel: handleCancelUnsavedDeleting });
  } else if (article.id && article.draft) {
    draftService.deleteDraft(article, { cb: redirectAfterDelete });
  } else if (article.id) {
    dispatchDeleteArticle(article.id);
  }
};

const redirectAfterDelete = () => router.push({ name: navigationService.isOpenedFrom(RETENTION) ? RETENTION : DRAFTS });

const handleCancelUnsavedDeleting = () => (isDeleting.value = false);

const handleVideoLoadedData = () => commitSetArticleEditorIsPublishingDisabled(false);

// HELPERS
const pushToUndoArticles = (savedArticle: Article) => {
  articleUndoStack.value.push(savedArticle);

  clearTimeout(timeout.value);
  timeout.value = setTimeout(async () => {
    await articleEditService.saveAsDraft();
  }, 0);
};

const revertToPreviousArticleState = () => {
  if (articleUndoStack.value.length > 0) {
    const revertedArticle = articleUndoStack.value[articleUndoStack.value.length - 1];
    store.commit('setArticleEditorArticle', revertedArticle);
    articleUndoStack.value.pop();
  }
};

const init = async () => {
  try {
    const res = await apiService.getWorkspaces();

    const workspaces = res.data.items.map((item: any) => Workspace.parse(item));
    articleEditService.setState('workspaces', workspaces);

    let workspace = articleEditService.articleWorkspace;
    if (!workspace && getArticleEditor.value.article) {
      workspace = workspaces.find((ws: Workspace) => getArticleEditor.value.article.workspace === ws.id);
    }

    dynamicTextEditorKey.value = getArticleEditor.value.article.original
      ? getArticleEditor.value.article.original
      : getArticleEditor.value.article.id;

    articleEditService.setState('workspace', workspace);
  } catch (err) {
    notificationService.error('Can not load workspaces');
    log.error(err);
  }

  if (store.state.workspace) {
    articleEditService.setState('workspace', store.state.workspace);
  }
};

const undoKeyboardHandler = (event: KeyboardEvent): boolean => {
  if ((event.ctrlKey || event.metaKey) && event.keyCode === 90) {
    revertToPreviousArticleState();
    return false;
  }
};

onMounted(async () => {
  await init();
  extensionService.addMessageListener(onMessageFromExtension);
});

onMounted(() => {
  setReferences();
  EventBus.$on('on-paragraphs-changes', pushToUndoArticles);
  EventBus.$on('updateReferences', () => {
    setReferences();
  });
});

onMounted(() => {
  window.addEventListener('keydown', undoKeyboardHandler);
});

onBeforeUnmount(() => {
  extensionService.removeMessageListener(onMessageFromExtension);
  window.removeEventListener('keydown', undoKeyboardHandler);
  EventBus.$off('updateReferences');
  EventBus.$off('on-paragraphs-changes');
});

watch(
  (): SearchByTextProperty[] => getV.value,
  () => {
    if (getV.value.length) {
      dynamicTextEditorKey.value = getV.value[0].startIndex.toString();
    }
  },
  { deep: true }
);

watch(
  getArticleEditor.value.article,
  (value: Article, oldValue: Article) => {
    if ((!!value.id && !oldValue.id) || value.id !== oldValue.id) {
      init();
    }
  },
  { deep: true }
);

return { __sfc: true,log, articleUndoStack, isDeleting, timeout, dynamicTextEditorKey, uploadButton, videoPlayer, getArticleEditor, getV, getArticleMode, getIsOcrMode, getIsMobile, editorKey, getIsDefaultVideoEditorState, setReferences, chooseFile, videoTimeUpdate, trim, moveParagraph, paragraphPlaying, playerState, playedRange, videoDurationDetected, insertParagraph, onClickAddRecording, onMessageFromExtension, deleteArticle, redirectAfterDelete, handleCancelUnsavedDeleting, handleVideoLoadedData, pushToUndoArticles, revertToPreviousArticleState, init, undoKeyboardHandler, ArticleTagsDialog, ArticleTextEditor, UploadButton, EmptyStateCard, ArticlePlayer, EditorNavbar }
}

})